<script setup>
const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
})

const resolveImageUrl = (url) => {
  if (!url) return ""
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith("/media/")) return url
  return url
}
</script>

<template>
  <article class="project-card">
    <NuxtLink :to="`/projects/${props.project.slug}`" class="project-card__click-area">
      <div class="project-card__image-wrap">
        <img
          v-if="props.project.image_url"
          :src="resolveImageUrl(props.project.image_url)"
          :alt="props.project.title"
          class="project-card__image"
          loading="lazy"
        >
      </div>
      <div class="project-card__content">
        <h5 class="project-card__title">{{ props.project.title }}</h5>
        <div class="project-card__description" v-html="props.project.short_description"></div>
        <div v-if="props.project.tags?.length" class="project-card__tags">
          <span v-for="tag in props.project.tags" :key="`${props.project.slug}-${tag}`" class="project-tag">
            {{ tag }}
          </span>
        </div>
        <span class="project-card__link">
          Детальніше
        </span>
      </div>
    </NuxtLink>
  </article>
</template>

<style lang="scss" scoped>
@use "/assets/scss/variables";

.project-card {
  border: 1px solid #e6e6e6;
  border-radius: 1em;
  overflow: hidden;
  background: #fff;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.project-card:hover,
.project-card:focus-within {
  border-color: #d8d8d8;
  box-shadow: 0 0.9em 2.2em rgba(15, 15, 15, 0.1);
  transform: translateY(-0.18em);
}

.project-card__click-area {
  color: inherit;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 100%;
  text-decoration: none;
}

.project-card__image-wrap {
  width: 100%;
  aspect-ratio: 2.2 / 1;
  background: linear-gradient(135deg, #f8f8f8 0%, #eeeeee 100%);
  overflow: hidden;
}

.project-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: filter 0.22s ease, transform 0.22s ease;
}

.project-card:hover .project-card__image,
.project-card:focus-within .project-card__image {
  filter: brightness(0.98) saturate(1.04);
  transform: scale(1.015);
}

.project-card__content {
  padding: 1.25em;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.875em;
}

.project-card__title {
  font-size: 1.25em;
  line-height: 1.35;
}

.project-card__description {
  color: #555;
  line-height: 1.5;
  font-size: 0.94em;
}

.project-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
}

.project-tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #f2f2f2;
  color: #1f1f1f;
  font-size: 0.75em;
  font-weight: 600;
  padding: 0.42em 0.8em;
}

.project-card__link {
  margin-top: auto;
  color: #0f0f0f;
  display: inline-block;
  width: fit-content;
  font-weight: 700;
  text-decoration: none;
  transition: color 0.2s ease-in-out;
}

.project-card:hover .project-card__link,
.project-card:focus-within .project-card__link {
  color: variables.$accent;
}

@media (prefers-reduced-motion: reduce) {
  .project-card,
  .project-card__image,
  .project-card__link {
    transition: none;
  }

  .project-card:hover,
  .project-card:focus-within,
  .project-card:hover .project-card__image,
  .project-card:focus-within .project-card__image {
    transform: none;
  }
}

@media screen and (max-width: 767px) {
  .project-card {
    border-radius: 0.875em;
    min-height: 0;
    height: auto;
  }

  .project-card__click-area {
    flex: 0 0 auto;
    min-height: 0;
  }

  .project-card__content {
    padding: 1.125em;
    flex: 0 0 auto;
    gap: 0.75em;
  }

  .project-card__title {
    font-size: 1.18em;
  }

  .project-card__description {
    font-size: 0.92em;
  }

  .project-card__tags {
    gap: 0.4em;
  }

  .project-tag {
    padding: 0.36em 0.7em;
  }

  .project-card__link {
    color: variables.$accent;
    margin-top: 0.7em;
  }
}
</style>
